import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@shared/api';
import { ModelLogo, ProviderLogo, providerName } from '@shared/brand';
import { compact, idr } from '@shared/format';
import { Alert, Badge, Card, Empty, Icon, Loading, PageHeader, Segmented, Select, type SelectOption } from '../components/ui';
import { MODALITY_LABEL, contextLabel, modalityOf, playgroundHref, providerOf, unitPrice, type CatalogModel, type Modality } from '../catalog';
import '../styles/models.css';

type Kind = 'all' | Modality;
type Sort = 'name' | 'cheap' | 'context';
type Cap = 'tools' | 'vision' | 'reasoning' | 'json_schema';
type View = 'grid' | 'table';

const CAP_OPTIONS: ReadonlyArray<SelectOption<Cap | 'any'>> = [
  { value: 'any', label: 'Semua kemampuan', icon: <Icon name="boxes" size={15} /> },
  { value: 'tools', label: 'Tools', description: 'Function calling', icon: <Icon name="plug" size={15} /> },
  { value: 'vision', label: 'Input gambar', description: 'Bisa membaca gambar', icon: <Icon name="image" size={15} /> },
  { value: 'reasoning', label: 'Reasoning', description: 'Berpikir sebelum menjawab', icon: <Icon name="spark" size={15} /> },
  { value: 'json_schema', label: 'JSON schema', description: 'Output terstruktur', icon: <Icon name="braces" size={15} /> },
];

const SORT_OPTIONS: ReadonlyArray<SelectOption<Sort>> = [
  { value: 'name', label: 'Urut: nama', text: 'nama', description: 'A sampai Z' },
  { value: 'cheap', label: 'Urut: termurah', text: 'termurah', description: 'Harga terendah per jenis model' },
  { value: 'context', label: 'Urut: konteks', text: 'konteks', description: 'Jendela konteks terbesar dulu' },
];

const MODALITY_ORDER: Record<Modality, number> = { text: 0, embedding: 1, image: 2, video: 3 };
const VIEW_KEY = 'aiagg.models.view';

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'grid';
  } catch {
    return 'grid';
  }
}

function toggle<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function Models() {
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<Kind>('all');
  const [providers, setProviders] = useState<Set<string>>(new Set());
  const [cap, setCap] = useState<Cap | ''>('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('name');
  const [view, setView] = useState<View>(readView);

  useEffect(() => {
    api<{ data: CatalogModel[] }>('/v1/models')
      .then((result) => setModels(result.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // storage blocked
    }
  }, [view]);

  const counts = useMemo(() => {
    const result: Record<Kind, number> = { all: models.length, text: 0, image: 0, video: 0, embedding: 0 };
    for (const m of models) result[modalityOf(m)] += 1;
    return result;
  }, [models]);

  const providerList = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of models) {
      if (kind !== 'all' && modalityOf(m) !== kind) continue;
      map.set(providerOf(m), (map.get(providerOf(m)) ?? 0) + 1);
    }
    return [...map.entries()].map(([code, count]) => ({ code, count }));
  }, [models, kind]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const hits = models.filter(
      (m) =>
        (kind === 'all' || modalityOf(m) === kind) &&
        (providers.size === 0 || providers.has(providerOf(m))) &&
        (!cap || Boolean(m.capabilities[cap])) &&
        (!q ||
          m.id.toLowerCase().includes(q) ||
          m.display_name.toLowerCase().includes(q) ||
          providerName(providerOf(m)).toLowerCase().includes(q)),
    );
    return hits.sort((a, b) => {
      if (sort === 'context') return b.context_window - a.context_window || a.display_name.localeCompare(b.display_name);
      if (sort === 'cheap') return MODALITY_ORDER[modalityOf(a)] - MODALITY_ORDER[modalityOf(b)] || unitPrice(a) - unitPrice(b);
      return a.display_name.localeCompare(b.display_name);
    });
  }, [models, kind, providers, cap, search, sort]);

  const filtered = kind !== 'all' || providers.size > 0 || cap !== '' || search.trim() !== '';
  const reset = () => {
    setKind('all');
    setProviders(new Set());
    setCap('');
    setSearch('');
  };

  if (loading) return <Loading label="Memuat katalog" />;

  const providerTotal = new Set(models.map(providerOf)).size;

  return (
    <>
      <PageHeader
        title="Model"
        subtitle={`${counts.all} model dari ${providerTotal} provider, semuanya bisa dipanggil dengan satu API key. Harga dalam Rupiah.`}
      />

      {error && <Alert kind="error">{error}</Alert>}

      <div className="mx-toolbar">
        <div className="mx-top">
          <div className="search mx-search">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari model atau provider" aria-label="Cari model" />
          </div>
          <span className="mx-viewtoggle">
            <Segmented
              label="Tampilan"
              options={[
                { id: 'grid', label: <span className="mx-view" title="Kartu"><Icon name="grid" size={16} /></span> },
                { id: 'table', label: <span className="mx-view" title="Tabel"><Icon name="list" size={16} /></span> },
              ]}
              value={view}
              onChange={setView}
            />
          </span>
        </div>
        <div className="mx-controls">
          <div className="mx-kinds">
            <Segmented
              label="Jenis model"
              options={[
                { id: 'all', label: <>Semua <span className="mx-n">{counts.all}</span></> },
                { id: 'text', label: <>Teks <span className="mx-n">{counts.text}</span></> },
                { id: 'image', label: <>Gambar <span className="mx-n">{counts.image}</span></> },
                { id: 'video', label: <>Video <span className="mx-n">{counts.video}</span></> },
                ...(counts.embedding > 0
                  ? [{ id: 'embedding' as const, label: <>Embedding <span className="mx-n">{counts.embedding}</span></> }]
                  : []),
              ]}
              value={kind}
              onChange={(next) => {
                setKind(next);
                setProviders(new Set());
              }}
            />
          </div>
          <Select
            className="mx-select mx-select-cap"
            value={cap || 'any'}
            onChange={(next) => setCap(next === 'any' ? '' : next)}
            options={CAP_OPTIONS}
            aria-label="Filter kemampuan"
          />
          <Select className="mx-select mx-select-sort" value={sort} onChange={setSort} options={SORT_OPTIONS} aria-label="Urutkan" />
        </div>
      </div>

      <div className="mx-providers" role="group" aria-label="Filter provider">
        {providerList.map((p) => (
          <button
            key={p.code}
            type="button"
            className={providers.has(p.code) ? 'mx-chip on' : 'mx-chip'}
            aria-pressed={providers.has(p.code)}
            onClick={() => setProviders((set) => toggle(set, p.code))}
          >
            <ProviderLogo provider={p.code} size={16} bare />
            {providerName(p.code)}
            <span className="mx-n">{p.count}</span>
          </button>
        ))}
        <span className="mx-meta">
          <span className="mx-count">
            {shown.length} dari {models.length} model
          </span>
          {filtered && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={reset}>
              <Icon name="close" size={14} />
              Reset
            </button>
          )}
        </span>
      </div>

      {shown.length === 0 ? (
        <Card className="mb">
          <Empty icon="search" action={filtered ? <button className="btn btn-sm" onClick={reset}>Reset filter</button> : undefined}>
            {models.length === 0 ? 'Belum ada model aktif.' : 'Tidak ada model yang cocok dengan filter ini.'}
          </Empty>
        </Card>
      ) : view === 'grid' ? (
        <div className="mx-grid">
          {shown.map((m) => (
            <ModelCard key={m.id} model={m} />
          ))}
        </div>
      ) : (
        <Card className="mx-table-card">
          <ModelTable models={shown} />
        </Card>
      )}

      <p className="mx-note">
        Model teks ditagih per token: input dan output punya tarif masing-masing, dan input dari cache lebih murah. Model embedding
        hanya ditagih input. Model gambar ditagih per gambar yang berhasil dibuat. Tarif video per detik adalah perkiraan untuk 720p. Setiap request memakai tarif yang berlaku saat
        request dijalankan.
      </p>
    </>
  );
}

function CopyId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={copied ? 'mx-copy done' : 'mx-copy'}
      title="Salin ID model"
      aria-label={`Salin ${value}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        } catch {
          setCopied(false);
        }
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} size={14} />
    </button>
  );
}

function CapTags({ model }: { model: CatalogModel }) {
  const c = model.capabilities;
  const text = modalityOf(model) === 'text';
  const tags = [
    text && c.tools ? 'Tools' : null,
    c.vision ? 'Input gambar' : null,
    c.reasoning ? 'Reasoning' : null,
    c.json_schema ? 'JSON' : null,
  ].filter((tag): tag is string => tag !== null);
  if (tags.length === 0) return null;
  return (
    <div className="mx-tags">
      {tags.map((tag) => (
        <span key={tag} className="tag" title={tag === 'JSON' ? 'Mendukung JSON schema (structured output)' : undefined}>
          {tag}
        </span>
      ))}
    </div>
  );
}

function PriceBlock({ model }: { model: CatalogModel }) {
  const p = model.pricing;
  const mode = modalityOf(model);
  if (!p) return <div className="mx-price mx-price-none">Harga belum tersedia</div>;
  if (mode === 'image') {
    return (
      <div className="mx-price">
        <div>
          <span>Per gambar</span>
          <b>{idr(p.per_image)}</b>
        </div>
      </div>
    );
  }
  if (mode === 'embedding') {
    return (
      <div className="mx-price">
        <div className="mx-price-cap">Per 1 juta token</div>
        <div>
          <span>Input</span>
          <b>{idr(p.input_per_1m)}</b>
        </div>
      </div>
    );
  }
  if (mode === 'video') {
    return (
      <div className="mx-price">
        <div>
          <span>Per detik, 720p</span>
          <b>≈ {idr(p.per_second_720p)}</b>
        </div>
        <div>
          <span>Per 1 jt token video</span>
          <b>{idr(p.per_1m_video_tokens)}</b>
        </div>
      </div>
    );
  }
  return (
    <div className="mx-price">
      <div className="mx-price-cap">Per 1 juta token</div>
      <div>
        <span>Input</span>
        <b>{idr(p.input_per_1m)}</b>
      </div>
      <div>
        <span>Output</span>
        <b>{idr(p.output_per_1m)}</b>
      </div>
      {p.cached_input_per_1m !== undefined && (
        <div>
          <span>Cache</span>
          <b className="mx-dim">{idr(p.cached_input_per_1m)}</b>
        </div>
      )}
    </div>
  );
}

function specLine(model: CatalogModel): string {
  const mode = modalityOf(model);
  if (mode === 'image') return `Hingga ${model.max_output_tokens} gambar per request`;
  if (mode === 'video') return `Durasi hingga ${model.max_output_tokens} detik`;
  if (mode === 'embedding') return `Input hingga ${contextLabel(model)} token · ${model.max_output_tokens} dimensi`;
  return `Konteks ${contextLabel(model)} · output maks ${compact(model.max_output_tokens)} token`;
}

function ModelCard({ model }: { model: CatalogModel }) {
  const mode = modalityOf(model);
  return (
    <article className="mx-card">
      <header className="mx-card-head">
        <ModelLogo model={model.id} family={model.family} provider={providerOf(model)} size={40} />
        <div className="grow" style={{ minWidth: 0 }}>
          <h3 className="mx-name">{model.display_name}</h3>
          <div className="mx-provider">{providerName(providerOf(model))}</div>
        </div>
        {model.status === 'DEPRECATED' ? (
          <Badge tone="warn">Segera pensiun</Badge>
        ) : (
          mode !== 'text' && <span className={`badge mx-mod-${mode}`}>{MODALITY_LABEL[mode]}</span>
        )}
      </header>

      <div className="mx-spec">{specLine(model)}</div>
      <PriceBlock model={model} />
      <CapTags model={model} />

      <footer className="mx-card-foot">
        <code className="mx-id" title={model.id}>
          {model.id}
        </code>
        <CopyId value={model.id} />
        <span className="grow" />
        <Link className="btn btn-sm" to={playgroundHref(model)}>
          {mode === 'embedding' ? 'Cara pakai' : 'Coba'}
          <Icon name="arrow" size={14} />
        </Link>
      </footer>
    </article>
  );
}

function ModelTable({ models }: { models: CatalogModel[] }) {
  return (
    <div className="table-wrap">
      <table className="mx-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>Provider</th>
            <th className="num">Konteks</th>
            <th className="num">Input / 1 jt</th>
            <th className="num">Output / 1 jt</th>
            <th>Kemampuan</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {models.map((m) => {
            const mode = modalityOf(m);
            const p = m.pricing;
            return (
              <tr key={m.id}>
                <td className="mt-model">
                  <div className="row" style={{ gap: 12 }}>
                    <ModelLogo model={m.id} family={m.family} provider={providerOf(m)} size={30} />
                    <div style={{ minWidth: 0 }}>
                      <div className="mx-t-name">
                        {m.display_name}
                        {mode !== 'text' && <span className={`badge mx-mod-${mode}`}>{MODALITY_LABEL[mode]}</span>}
                      </div>
                      <div className="mx-t-id">
                        <code>{m.id}</code>
                        <CopyId value={m.id} />
                      </div>
                    </div>
                  </div>
                </td>
                <td className="nowrap mt-prov">
                  <span className="row" style={{ gap: 8 }}>
                    <ProviderLogo provider={providerOf(m)} size={16} bare />
                    {providerName(providerOf(m))}
                  </span>
                </td>
                <td className={mode === 'text' || mode === 'embedding' ? 'num mt-ctx' : 'num mt-ctx none'}>
                  {mode === 'text' || mode === 'embedding' ? contextLabel(m) : <span className="muted">—</span>}
                </td>
                {!p ? (
                  <td className="num muted mt-price" colSpan={2}>
                    —
                  </td>
                ) : mode === 'text' || mode === 'embedding' ? (
                  <>
                    <td className="num strong mt-in">{idr(p.input_per_1m)}</td>
                    <td className="num strong mt-out">{mode === 'embedding' ? <span className="muted">—</span> : idr(p.output_per_1m)}</td>
                  </>
                ) : (
                  <td className="num mt-price" colSpan={2}>
                    {mode === 'image' ? (
                      <>
                        <b className="mx-strong">{idr(p.per_image)}</b> <span className="muted">per gambar</span>
                      </>
                    ) : (
                      <>
                        <b className="mx-strong">≈ {idr(p.per_second_720p)}</b> <span className="muted">per detik, 720p</span>
                      </>
                    )}
                  </td>
                )}
                <td className="mt-caps">
                  <CapTags model={m} />
                </td>
                <td className="num mt-act">
                  <Link className="btn btn-sm" to={playgroundHref(m)}>
                    {mode === 'embedding' ? 'Cara pakai' : 'Coba'}
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
